import { Router, Routes, UrlMatchResult, UrlSegment } from '@angular/router';

import { CanDeactivateGuard } from './modules/create/guards/can-deactivate.guard';
import { CommonUtility } from './common/utils/commonUtils';
import { FetchCMSContentService } from './common/services/fetch-cms-content/fetch-cms-content.service';
import { LoginService } from './common/services/login-service/login.service';
import { RequestAccessGuard } from './common/guards/request-access.guard';
import { ContactOnboardingGuard } from './common/guards/contact-onboarding.guard';
import { SymphonyIdGuard } from './common/guards/symphony-id.guard';
import { ROUTER_CONSTANTS } from './common/constants/router.constants';
import { FEATURE_FLAGS, USER_TYPES } from './common/constants/global.constant';
import { endPoints } from './common/constants/endpoints';
import { forkJoin } from 'rxjs';
import { inject } from '@angular/core';
import { AppConfigService } from './common/services/app-config.service/app-config.service';

// `salesforce_labels` holds the field/component labels keyed by Salesforce identifier.
const SALESFORCE_CMS_APP_IDS = ['salesforce'];

const CREATE_CASE_CMS_CONTENT_RESOLVER = () =>
  forkJoin([
    inject(FetchCMSContentService).getCMSContent(endPoints.createContent, [
      'create',
      'common',
      'details',
      'salesforce-labels',
    ]),
    inject(FetchCMSContentService).getCMSContent(
      endPoints.salesforceContent(),
      SALESFORCE_CMS_APP_IDS,
    ),
  ]);

const FALLBACK_REDIRECT = () => {
  const loginService = inject(LoginService);

  if (loginService.hasAccess) {
    return ROUTER_CONSTANTS.error;
  }

  return loginService.isLoggedIn
    ? ROUTER_CONSTANTS.noAccess
    : ROUTER_CONSTANTS.login;
};

export const routes: Routes = [
  {
    path: `${ROUTER_CONSTANTS.dashboard}`,
    loadComponent: () =>
      import('./modules/dashboard/components/dashboard.component').then(
        (m) => m.DashboardComponent,
      ),
    canMatch: [() => inject(LoginService).hasAccess, SymphonyIdGuard],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(
            endPoints.dashboardContent,
            ['common', 'dashboard', 'salesforce-labels'],
          ),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
  },
  {
    path: `ufa/location/:locationId`,
    loadComponent: () =>
      import(
        './modules/dashboard/components/ufalocation-details/ufalocation-details.component'
      ).then((m) => m.UfaLocationDetailsPageComponent),
    canMatch: [() => inject(LoginService).hasAccess, SymphonyIdGuard],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(
            endPoints.gffCRMContent,
            ['common', 'gffCRM', 'details', 'dashboard', 'salesforce-labels'],
          ),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
  },
  {
    path: `profile/:id`,
    loadComponent: () =>
      import(
        './modules/detail/components/profile-summary/summary-tab.component'
      ).then((m) => m.CustomerProfileSummaryComponent),
    canMatch: [() => inject(LoginService).hasAccess, SymphonyIdGuard],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(
            endPoints.gffCRMContent,
            ['common', 'details', 'salesforce-labels'],
          ),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
  },
  {
    path: `ccp/:id`,
    loadComponent: () =>
      import(
        './modules/detail/components/ccp-accounts/summary-tab/ccp-account-tab.component'
      ).then((m) => m.CcpAccountTabComponent),
    canMatch: [() => inject(LoginService).hasAccess, SymphonyIdGuard],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(
            endPoints.gffCRMContent,
            ['common', 'details', 'salesforce-labels'],
          ),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
  },
  {
    path: `${ROUTER_CONSTANTS.viewAccount}/:accountNumber/contacts`,
    loadComponent: () =>
      import(
        './modules/detail/components/contact-list-table/contact-list-table.component'
      ).then((m) => m.ContactListTableComponent),
    canMatch: [() => inject(LoginService).hasAccess],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(
            endPoints.gffCRMContent,
            ['common', 'gffCRM', 'details', 'salesforce-labels'],
          ),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
  },
  {
    path: `${ROUTER_CONSTANTS.viewAccount}/:accountNumber/hierarchy`,
    loadComponent: () =>
      import(
        './modules/detail/components/account-hierarchy/account-hierarchy.component'
      ).then((m) => m.AccountHierarchyComponent),
    canMatch: [() => inject(LoginService).hasAccess],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(
            endPoints.gffCRMContent,
            ['common', 'gffCRM', 'details', 'salesforce-labels'],
          ),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
  },
  {
    path: `${ROUTER_CONSTANTS.viewAccount}/:accountNumber`,
    loadComponent: () =>
      import(
        './modules/detail/components/account-detail/account-detail.component'
      ).then((m) => m.AccountDetailComponent),
    canMatch: [() => inject(LoginService).hasAccess],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(
            endPoints.gffCRMContent,
            ['common', 'gffCRM', 'details', 'salesforce-labels'],
          ),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
  },
  {
    path: `${ROUTER_CONSTANTS.viewAccount}/:accountNumber/${ROUTER_CONSTANTS.productDetail}/:productID`,
    loadComponent: () =>
      import(
        './modules/detail/components/product-detail/product-detail.component'
      ).then((m) => m.ProductDetailComponent),
    canMatch: [() => inject(LoginService).hasAccess],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(
            endPoints.gffCRMContent,
            ['common', 'gffCRM', 'details', 'salesforce-labels'],
          ),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
  },
  {
    path: `${ROUTER_CONSTANTS.preview}`,
    loadComponent: () =>
      import(
        './modules/detail/components/file-preview-page/file-preview-page.component'
      ).then((m) => m.FilePreviewPageComponent),
    canMatch: [() => inject(LoginService).hasAccess],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(
            endPoints.gffCRMContent,
            ['common', 'gffCRM', 'details', 'salesforce-labels'],
          ),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
  },
  {
    path: `${ROUTER_CONSTANTS.gffCRM}`,
    loadComponent: () =>
      import(
        './modules/dashboard/components/gff-crm-accounts-dashboard/gff-crm-accounts-dashboard.component'
      ).then((m) => m.GFFCRMAccountsDashboardComponent),
    canMatch: [() => inject(LoginService).hasAccess],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(
            endPoints.gffCRMContent,
            ['common', 'gffCRM', 'salesforce-labels'],
          ),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
  },
  {
    path: `${ROUTER_CONSTANTS.admin}`,
    data: {
      loader: true,
    },
    canActivate: [
      () => {
        const loginService = inject(LoginService);
        const router = inject(Router);
        if (
          loginService.hasAccess &&
          loginService.userProfile?.userDetails.isAdmin
        ) {
          return true;
        } else {
          return router.createUrlTree([ROUTER_CONSTANTS.noAccess]);
        }
      },
    ],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(endPoints.adminContent, [
            'common',
            'admin',
            'salesforce-labels',
          ]),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
    loadChildren: () => [
      {
        path: '',
        loadComponent: (): Promise<any> =>
          import('./modules/admin/component/admin/admin.component').then(
            (m) => m.AdminComponent,
          ),
      },
      {
        path: ROUTER_CONSTANTS.users,
        loadComponent: (): Promise<any> =>
          import(
            './modules/admin/component/manage-users/manage-users.component'
          ).then((m) => m.ManageUsersComponent),
      },
      {
        path: `${ROUTER_CONSTANTS.createUser}`,
        loadComponent: (): Promise<any> =>
          import(
            './modules/admin/component/create-user/create-user.component'
          ).then((m) => m.CreateUserComponent),
      },
    ],
  },
  {
    path: `${ROUTER_CONSTANTS.detail}/:caseId`,
    loadComponent: () =>
      import('./modules/detail/components/case-detail.component').then(
        (m) => m.CaseDetailComponent,
      ),
    canMatch: [() => inject(LoginService).hasAccess, SymphonyIdGuard],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(
            endPoints.detailsContent,
            ['common', 'details', 'salesforce-labels'],
          ),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
  },
  {
    // MVP4 Dynamic Form for all accounts
    path: `${ROUTER_CONSTANTS.createCase}`,
    loadComponent: () => {
      return import(
        './modules/create/components/dynamic-form-wrapper/dynamic-form-wrapper.component'
      ).then((m) => m.DynamicFormWrapperComponent);
    },
    canMatch: [
      () => inject(LoginService).hasAccess,
      SymphonyIdGuard,
      () => {
        const loginService = inject(LoginService);
        const appConfigService = inject(AppConfigService);
        const brokerageForm: string =
          appConfigService.appConfig?.featureFlags?.brokerageForm || '';
        const features =
          loginService.userProfile?.accountDetails?.[0]?.features ?? [];
        const hasV4Feature: boolean =
          features?.some(
            (feature: any) =>
              feature.code?.toLowerCase() === brokerageForm?.toLowerCase(),
          ) ?? false;
        return hasV4Feature;
      },
    ],
    canDeactivate: [CanDeactivateGuard],
    resolve: {
      cmsContent: CREATE_CASE_CMS_CONTENT_RESOLVER,
    },
  },
  {
    // MVP3 Dynamic Form for all accounts
    path: `${ROUTER_CONSTANTS.createCase}`,
    loadComponent: () => {
      // MVP3 Form for Symphony HLD/GLD accounts
      return import(
        './modules/create/components/transactional-support-case/transactional-support-case.component'
      ).then((m) => m.TransactionalSupportCaseComponent);
    },
    canMatch: [
      () => inject(LoginService).hasAccess,
      SymphonyIdGuard,
      () => {
        const loginService = inject(LoginService);
        const isDynamicV3Form =
          loginService.userProfile?.accountDetails?.[0]?.features?.some(
            (feature) => feature.code === FEATURE_FLAGS.dynamicCaseV3Form,
          ) ?? false;
        return isDynamicV3Form;
      },
    ],
    canDeactivate: [CanDeactivateGuard],
    resolve: {
      cmsContent: CREATE_CASE_CMS_CONTENT_RESOLVER,
    },
  },
  {
    // MVP1 Brokerage RFI form for Apple account (DCM)
    matcher: (url: UrlSegment[]): UrlMatchResult | null => {
      const isAppleAccount: boolean =
        CommonUtility.getAccountName() === 'Apple' &&
        CommonUtility.isDcmPortalSource();
      // if user is symphony user then open new case form
      if (
        url.length &&
        url[0].path === ROUTER_CONSTANTS.createCase &&
        isAppleAccount
      ) {
        return {
          consumed: url,
        };
      }
      return null;
    },
    loadComponent: () => {
      // MVP1 Brokerage RFI form for Apple account (DCM)
      return import(
        './modules/create/components/brokerage-rfi/brokerage-rfi.component'
      ).then((m) => m.BrokerageRfiComponent);
    },
    canMatch: [
      () => {
        const loginService = inject(LoginService);
        const isAppleAccount: boolean =
          CommonUtility.getAccountName() === 'Apple' &&
          CommonUtility.isDcmPortalSource() &&
          loginService.userProfile?.userDetails.userType.toLowerCase() !==
            'employee';
        return loginService.hasAccess && isAppleAccount;
      },
      SymphonyIdGuard,
    ],
    canDeactivate: [CanDeactivateGuard],
    resolve: {
      cmsContent: CREATE_CASE_CMS_CONTENT_RESOLVER,
    },
  },
  {
    // MVP1 Brokerage form for DCM accounts
    path: `${ROUTER_CONSTANTS.createCase}`,
    loadComponent: () =>
      import(
        './modules/create/components/create-case/create-case.component'
      ).then((m) => m.CreateCaseComponent),
    canMatch: [
      () => {
        const loginService = inject(LoginService);
        const isDcmPortalSource: boolean = CommonUtility.isDcmPortalSource();
        return (
          loginService.hasAccess &&
          isDcmPortalSource &&
          loginService.userProfile?.userDetails?.userType?.toLowerCase() !==
            USER_TYPES.customer
        );
      },
      SymphonyIdGuard,
    ],
    canDeactivate: [CanDeactivateGuard],
    resolve: {
      cmsContent: CREATE_CASE_CMS_CONTENT_RESOLVER,
    },
  },
  {
    path: `${ROUTER_CONSTANTS.createAccount}`,
    loadComponent: () =>
      import(
        './modules/detail/components/profile-creation/profile-creation.component'
      ).then((m) => m.ProfileCreationComponent),
    canMatch: [() => inject(LoginService).canGFFCRMUserCreate()],
    resolve: {
      cmsContent: () =>
        forkJoin([
          inject(FetchCMSContentService).getCMSContent(
            endPoints.detailsContent,
            ['common', 'details', 'salesforce-labels'],
          ),
          inject(FetchCMSContentService).getCMSContent(
            endPoints.salesforceContent(),
            SALESFORCE_CMS_APP_IDS,
          ),
        ]),
    },
  },
  {
    path: `${ROUTER_CONSTANTS.requestAccess}`,
    loadComponent: () =>
      import(
        './modules/request-access/components/request-access-form/request-access-form.component'
      ).then((m) => m.RequestAccessFormComponent),
    canMatch: [RequestAccessGuard],
  },
  {
    path: `${ROUTER_CONSTANTS.contactOnboarding}`,
    loadComponent: () =>
      import(
        './modules/contact-onboarding/components/contact-onboarding-form/contact-onboarding-form.component'
      ).then((m) => m.ContactOnboardingFormComponent),
    canMatch: [ContactOnboardingGuard],
  },
  {
    path: `${ROUTER_CONSTANTS.login}`,
    loadComponent: () =>
      import('./modules/login/login.component').then((m) => m.LoginComponent),
    canMatch: [() => !inject(LoginService).isLoggedIn],
    resolve: {
      cmsContent: () =>
        inject(FetchCMSContentService).getCMSContent(endPoints.globalContent, [
          'casemgmt_lndg_content',
        ]),
    },
  },
  {
    path: `${ROUTER_CONSTANTS.login}`,
    loadComponent: () =>
      import('./common/components/molecules/noaccess/noaccess.component').then(
        (m) => m.NoAccessComponent,
      ),
    canMatch: [
      () => inject(LoginService).isLoggedIn && !inject(LoginService).hasAccess,
    ],
  },
  {
    path: `${ROUTER_CONSTANTS.login}`,
    redirectTo: `${ROUTER_CONSTANTS.dashboard}`,
  },
  {
    path: `${ROUTER_CONSTANTS.error}`,
    loadComponent: () =>
      import('./common/components/molecules/error/error.component').then(
        (m) => m.ErrorComponent,
      ),
    canMatch: [() => inject(LoginService).hasAccess],
  },
  {
    path: `${ROUTER_CONSTANTS.maintenance}`,
    loadComponent: () =>
      import(
        './common/components/molecules/maintenance/maintenance.component'
      ).then((m) => m.MaintenanceComponent),
  },
  {
    path: '',
    pathMatch: 'full',
    children: [],
    canActivate: [
      () => {
        const loginService = inject(LoginService);
        const router = inject(Router);
        if (loginService.hasAccess) {
          if (loginService.state) {
            router.navigateByUrl(loginService.state);
            return false;
          } else {
            return router.createUrlTree([ROUTER_CONSTANTS.dashboard]);
          }
        } else {
          return false;
        }
      },
    ],
  },
  {
    path: `${ROUTER_CONSTANTS.noAccess}`,
    loadComponent: () =>
      import('./common/components/molecules/noaccess/noaccess.component').then(
        (m) => m.NoAccessComponent,
      ),
    canMatch: [() => !inject(LoginService).hasAccess],
  },
  {
    path: '**',
    redirectTo: FALLBACK_REDIRECT,
  },
];
